import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  useTheme,
  TextField,
} from "@mui/material";
import { IOrderItem, OrderStatus } from "../../../types/orders";
import { FC, useState } from "react";
import {
  Delete,
  Cancel as CancelIcon,
  Undo as UndoIcon,
} from "@mui/icons-material";
import useRemoveItem from "../../../services/useRemoveItem";
import {
  useCancelIndividualItem,
  useUncancelIndividualItem,
} from "../../../services/useCancelIndividualItem";

interface Props {
  fairId: number;
  item: IOrderItem;
  orderStatus: OrderStatus | "MIXED" | null;
  showCost?: boolean;
}

const OrderItem: FC<Props> = ({
  fairId,
  item,
  orderStatus,
  showCost = true,
}) => {
  const { mutate: removeItem } = useRemoveItem();
  const { mutate: cancelItem } = useCancelIndividualItem();
  const { mutate: uncancelItem } = useUncancelIndividualItem();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const handleRemove = () => {
    removeItem(item.id);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCancel = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = () => {
    cancelItem({
      orderItemId: item.id,
      reason: cancelReason || "Item não entregue pelo agricultor",
      fairId,
    });
    setCancelDialogOpen(false);
    setCancelReason("");
  };

  const handleUncancel = () => {
    uncancelItem({
      orderItemId: item.id,
      fairId,
    });
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
  };

  // Items podem ser removidos apenas se estiverem em pedidos PENDING e não cancelados
  const canRemove = orderStatus === "PENDING" && !item.is_canceled;
  const canCancel = orderStatus === "PAID" && !item.is_canceled;
  const canUncancel = item.is_canceled;

  return (
    <TableRow
      sx={{
        backgroundColor: item.is_canceled
          ? theme.palette.error.light
          : "transparent",
      }}
    >
      <TableCell>
        <Box
          sx={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
          }}
        >
          {item.is_canceled ? (
            <Tooltip
              title={`Cancelado: ${item.canceled_reason || "Sem motivo"}\nPor: ${item.canceled_by_username || "Desconhecido"}\nEm: ${new Date(item.canceled_at!).toLocaleString()}`}
            >
              <Chip
                icon={<CancelIcon />}
                label="Cancelado"
                color="error"
                size="small"
              />
            </Tooltip>
          ) : (
            <Chip label="Ativo" color="success" size="small" />
          )}
          {canCancel && (
            <IconButton onClick={handleCancel} size="small" color="warning">
              <CancelIcon />
            </IconButton>
          )}
          {canUncancel && (
            <IconButton onClick={handleUncancel} size="small" color="success">
              <UndoIcon />
            </IconButton>
          )}
          {canRemove && (
            <IconButton onClick={handleOpen} size="small">
              <Delete />
            </IconButton>
          )}
        </Box>
      </TableCell>
      <TableCell align="right">{item.amount}</TableCell>
      <TableCell>
        {item.product.base_product.name}
        {item.product.base_product.unit
          ? ` (${item.product.base_product.unit})`
          : ""}{" "}
        - {item.product.producer.name}
      </TableCell>
      {showCost ? (
        <TableCell align="right">
          {(item.amount * item.cost).toFixed(2)}
        </TableCell>
      ) : (
        <TableCell align="right" sx={{ visibility: "hidden" }}></TableCell>
      )}
      <TableCell align="right">
        {(item.amount * item.price).toFixed(2)}
      </TableCell>
      <Dialog open={open} onClose={handleClose}>
        <DialogContent>
          <DialogContentText>
            Você tem certeza que deseja remover esse item do pedido?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Não</Button>
          <Button onClick={handleRemove} autoFocus>
            Sim
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={handleCancelDialogClose}>
        <DialogContent>
          <DialogContentText>
            Você tem certeza que deseja cancelar esse item? Um crédito será
            gerado para a parceira urbana.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Motivo do cancelamento (opcional)"
            fullWidth
            variant="outlined"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Ex: Produto não disponível"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose}>Não</Button>
          <Button onClick={handleCancelConfirm} color="warning" autoFocus>
            Cancelar Item
          </Button>
        </DialogActions>
      </Dialog>
    </TableRow>
  );
};

export default OrderItem;
